import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";

function GDRoom() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { topic } = location.state || { topic: "General Discussion" };

  const [isRunning, setIsRunning] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);

  const [attention, setAttention] = useState(100);
  const [gazeCount, setGazeCount] = useState(0);
  const [postureCount, setPostureCount] = useState(0);
  const [status, setStatus] = useState("Initializing...");
  const [timeline, setTimeline] = useState([]);
  const [gdEnded, setGdEnded] = useState(false);

  // 🎥 START CAMERA + MIC
  const startMedia = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true
    });

    streamRef.current = stream;

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  };

  useEffect(() => {
    startMedia();
  }, []);

  // 🎥 CAMERA TOGGLE
  const toggleCamera = () => {
    const videoTrack = streamRef.current?.getVideoTracks()[0];
    if (!videoTrack) return;

    videoTrack.enabled = !cameraOn;
    setCameraOn(!cameraOn);
  };

  // 🎤 MIC TOGGLE
  const toggleMic = () => {
    const audioTrack = streamRef.current?.getAudioTracks()[0];
    if (!audioTrack) return;

    audioTrack.enabled = !micOn;
    setMicOn(!micOn);
  };

  // 📸 CAPTURE FRAME
  const captureFrame = async () => {
    if (!videoRef.current || videoRef.current.readyState !== 4) return null;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);

    return new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg")
    );
  };

  // 🤖 AI LOOP
  useEffect(() => {
    let interval;

    if (isRunning && cameraOn) {
      interval = setInterval(async () => {
        const blob = await captureFrame();
        if (!blob) return;

        const formData = new FormData();
        formData.append("file", blob);

        try {
          const res = await fetch("http://127.0.0.1:8000/analyze", {
            method: "POST",
            body: formData,
          });

          const data = await res.json();
          const time = new Date().toLocaleTimeString();

          if (data.gaze_off) {
            setGazeCount((p) => p + 1);
            setStatus("⚠️ Looking Away");
            setTimeline((prev) => [...prev, `${time} - Looked away`]);
          } else {
            setStatus("✅ Focused");
          }

          if (data.posture_bad) {
            setPostureCount((p) => p + 1);
            setTimeline((prev) => [...prev, `${time} - Bad posture`]);
          }

          setAttention(data.attention_score || 0);

        } catch (err) {
          console.log(err);
        }
      }, 2000);
    }

    return () => clearInterval(interval);
  }, [isRunning, cameraOn]);

  // 🛑 END GD
  const endGD = async () => {
    setIsRunning(false);

    const reportRes = await fetch("http://127.0.0.1:8000/final-report");
    const report = await reportRes.json();

    const result = {
      ...report,
      topic,
      timeline
    };

    localStorage.setItem("gd-result", JSON.stringify(result));
    setGdEnded(true);
  };

  // 🎛️ MAIN TOGGLE
  const handleToggle = () => {
    if (isRunning) {
      endGD();
    } else {
      setIsRunning(true);
    }
  };

  return (
    <div style={layout}>
      <Sidebar />
      <div style={content}>
        <Topbar />

        <h2>🧠 AI GD Room</h2>
        <div style={box}>{topic}</div>

        {/* 🔥 4 SCREEN GRID */}
        <div style={videoGrid}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={videoBox}>
              <video
                ref={i === 0 ? videoRef : null}
                autoPlay
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div style={badge}>
                {i === 0 ? status : "Waiting..."}
              </div>
            </div>
          ))}
        </div>

        {/* 🔥 CONTROLS */}
        <div style={controls}>
          <button onClick={toggleCamera} style={controlBtn}>
            {cameraOn ? "📷 Camera ON" : "🚫 Camera OFF"}
          </button>

          <button onClick={toggleMic} style={controlBtn}>
            {micOn ? "🎤 Mic ON" : "🔇 Mic OFF"}
          </button>
        </div>

        {/* 🔥 MAIN TOGGLE */}
        <div style={toggleContainer}>
          <span>{isRunning ? "LIVE" : "OFF"}</span>

          <div
            onClick={handleToggle}
            style={{
              ...toggle,
              background: isRunning ? "#22c55e" : "#444"
            }}
          >
            <div
              style={{
                ...circle,
                transform: isRunning ? "translateX(30px)" : "translateX(0px)"
              }}
            />
          </div>
        </div>

        <div style={metrics}>
          <p>Attention: {attention}%</p>
          <p>Gaze Issues: {gazeCount}</p>
          <p>Posture Issues: {postureCount}</p>
        </div>

        {gdEnded && (
          <div style={resultBtn} onClick={() => navigate("/result")}>
            VIEW RESULT
          </div>
        )}
      </div>
    </div>
  );
}

// 🎨 STYLES (UNCHANGED + ONE NEW)
const layout = { display: "flex", background: "#050505", color: "white" };
const content = { flex: 1, marginLeft: "250px", padding: "40px" };

const videoGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "10px"
};

const videoBox = {
  border: "2px solid #6366f1",
  height: "200px",
  position: "relative"
};

const badge = { position: "absolute", top: 10, left: 10 };

const controls = {
  display: "flex",
  gap: "10px",
  marginTop: "20px"
};

const controlBtn = {
  padding: "10px",
  background: "#111",
  color: "white",
  border: "1px solid #333",
  cursor: "pointer"
};

const toggleContainer = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "20px"
};

const toggle = {
  width: "60px",
  height: "30px",
  borderRadius: "20px",
  padding: "5px",
  cursor: "pointer"
};

const circle = {
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  background: "white",
  transition: "0.3s"
};

const metrics = { marginTop: "20px" };

const resultBtn = {
  marginTop: "20px",
  background: "#6366f1",
  padding: "10px",
  cursor: "pointer"
};

const box = { marginBottom: "10px" };

export default GDRoom;